/**
 * geostylerToMapLibre — our own thin GeoStyler → MapLibre converter (Path B).
 *
 * We deliberately do NOT use `geostyler-mapbox-parser`: its `writeStyle()` drops
 * URL / per-PNG icon images ("Only Sprite is supported") and explodes symbolizers
 * into opaque multi-layers. Icons are our most common case, so we walk the
 * GeoStyler `rules → symbolizers` ourselves and emit MapLibre layer fragments
 * directly, keeping icons first-class and the output structure predictable.
 *
 * Scope / contract:
 *  - Input is a renderer-neutral GeoStyler `Style` (the stored format — the single
 *    source of truth shared by web + RN mobile).
 *  - Output is an array of renderer fragments ({@link MapLibreStyleLayer}) WITHOUT
 *    `id`/`source`; the caller attaches the GeoJSON source and a layer id and slots
 *    each fragment into its layer model (see task 8 rewiring).
 *  - Per Option A, GeoStyler `filter`s become MapLibre layer filters — the engine
 *    picks each feature's icon/colour from a `row_data` field; we no longer rely on
 *    backend-resolved `feature.properties.icon`/`.color`.
 *  - Icon `image` paths are rewritten to loadable URLs at conversion time
 *    (`public-icons/…` → signed CDN URL) via {@link resolveIconUrl}.
 *
 * Mobile parity: the only platform-specific seam is image resolution, exposed via
 * `options.resolveImage` so RN can pass its own resolver while sharing the rest.
 */
import type {
  FillSymbolizer,
  Filter,
  IconSymbolizer,
  LineSymbolizer,
  MarkSymbolizer,
  RasterSymbolizer,
  Rule,
  ScaleDenominator,
  Style,
  Symbolizer,
  SymbolizerKind,
  TextSymbolizer
} from "geostyler-style";
import { resolveIconUrl } from "./cdnUtils";

/** The MapLibre layer types a GeoStyler symbolizer can map onto. */
export type MapLibreLayerType = "symbol" | "circle" | "line" | "fill" | "raster";

/**
 * A renderer-ready MapLibre layer fragment. It intentionally omits `id` and
 * `source` — the caller supplies those when attaching the layer to a map source.
 */
export interface MapLibreStyleLayer {
  type: MapLibreLayerType;
  /** MapLibre expression filter derived from the rule's GeoStyler filter. */
  filter?: unknown[];
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  minzoom?: number;
  maxzoom?: number;
  /** Provenance so the caller can build stable ids / debug the mapping. */
  metadata: {
    rule: string;
    ruleIndex: number;
    /** Index of this fragment within the rule (a rule can yield several). */
    partIndex: number;
    symbolizerKinds: SymbolizerKind[];
  };
}

export interface GeostylerToMapLibreOptions {
  /**
   * Resolves a symbolizer image reference to a loadable URL. Defaults to the web
   * {@link resolveIconUrl} (rewrites `public-icons/…` to the signed CDN URL). RN
   * mobile passes its own resolver here to keep converter logic shared.
   */
  resolveImage?: (ref: string) => string;
}

/**
 * Default MapLibre symbol-layer layout. Mirrors the current CM-table/geojson
 * symbol rendering so migrated layers keep their look: icons stay on top and are
 * never placement-culled, labels are optional.
 */
const SYMBOL_LAYOUT_DEFAULTS: Record<string, unknown> = {
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
  "symbol-placement": "point",
  "symbol-z-order": "source",
  "icon-optional": false,
  "text-optional": true,
  "symbol-sort-key": 1
};

/**
 * Web-Mercator scale-denominator at zoom 0 for a 0.28mm pixel (OGC standard,
 * matches geostyler-mapbox-parser). `zoom = log2(SCALE_ZOOM_0 / denominator)`.
 */
const SCALE_ZOOM_0 = 559082264.028717;

const clampZoom = (z: number): number => Math.min(24, Math.max(0, Math.round(z * 100) / 100));

/**
 * GeoStyler `scaleDenominator` (min inclusive, max exclusive) → MapLibre
 * `minzoom`/`maxzoom`. A larger denominator means more zoomed out, so the
 * denominator `max` yields `minzoom` and the denominator `min` yields `maxzoom`.
 */
function scaleDenominatorToZoom(sd?: ScaleDenominator): { minzoom?: number; maxzoom?: number } {
  if (!sd) return {};
  const out: { minzoom?: number; maxzoom?: number } = {};
  if (typeof sd.max === "number" && sd.max > 0) {
    out.minzoom = clampZoom(Math.log2(SCALE_ZOOM_0 / sd.max));
  }
  if (typeof sd.min === "number" && sd.min > 0) {
    out.maxzoom = clampZoom(Math.log2(SCALE_ZOOM_0 / sd.min));
  }
  return out;
}

/**
 * A comparison's first argument is a property KEY (string) → `["get", key]`;
 * anything already non-string is assumed to be a ready MapLibre accessor/literal.
 */
function toAccessor(key: unknown): unknown {
  return typeof key === "string" ? ["get", key] : key;
}

/**
 * Translates a GeoStyler {@link Filter} into a MapLibre expression filter. Returns
 * `undefined` for "match everything" (no filter). Unsupported operators are
 * skipped with a warning rather than throwing, so one bad rule can't break a map.
 */
export function translateFilter(filter?: Filter): unknown[] | undefined {
  if (filter == null) return undefined;
  // A bare boolean literal: `true` → no filter; `false` → match nothing.
  if (typeof filter === "boolean") return filter ? undefined : ["==", ["literal", 1], ["literal", 0]];
  if (!Array.isArray(filter)) {
    console.warn("[geostylerToMapLibre] Unsupported non-array filter; ignoring", filter);
    return undefined;
  }

  const [op, ...rest] = filter as unknown[];
  switch (op) {
    case "&&": {
      const parts = rest.map((f) => translateFilter(f as Filter)).filter(Boolean) as unknown[][];
      return parts.length ? ["all", ...parts] : undefined;
    }
    case "||": {
      const parts = rest.map((f) => translateFilter(f as Filter)).filter(Boolean) as unknown[][];
      return parts.length ? ["any", ...parts] : undefined;
    }
    case "!": {
      const inner = translateFilter(rest[0] as Filter);
      return inner ? ["!", inner] : undefined;
    }
    case "<=x<=": {
      const [key, min, max] = rest;
      const get = toAccessor(key);
      return ["all", [">=", get, min], ["<=", get, max]];
    }
    case "==":
    case "!=":
    case "<":
    case "<=":
    case ">":
    case ">=": {
      const [key, value] = rest;
      return [op, toAccessor(key), value];
    }
    case "*=": {
      // GeoStyler "like"/contains → MapLibre substring membership test.
      const [key, value] = rest;
      return ["in", value, toAccessor(key)];
    }
    default:
      console.warn(`[geostylerToMapLibre] Unsupported filter operator "${op as string}"; ignoring`);
      return undefined;
  }
}

/**
 * Converts a GeoStyler label template (`"Name {{country}}"`, double braces) into
 * the MapLibre `{token}` text-field form (`"Name {country}"`).
 */
export function translateLabel(label?: unknown): string | undefined {
  if (typeof label !== "string") return undefined;
  return label.replace(/\{\{\s*([^}]+?)\s*\}\}/g, "{$1}");
}

/** Assigns `value` to `target[key]` only when it is not `undefined`. */
function set(target: Record<string, unknown>, key: string, value: unknown): void {
  if (value !== undefined) target[key] = value;
}

/**
 * Resolves an icon `image` (string path or Sprite) to a URL via the resolver.
 *
 * An empty path yields undefined rather than being resolved: a category whose icon
 * has not been chosen yet would otherwise become `icon-image` of a resolved-empty
 * URL, which MapLibre reports as a missing image and then tries to fetch. Leaving
 * the property off draws nothing, which is the honest rendering of "no icon set".
 */
function resolveImageRef(image: IconSymbolizer["image"], resolveImage: (ref: string) => string): string | undefined {
  if (typeof image === "string") return image.trim() ? resolveImage(image) : undefined;
  if (image && typeof image === "object" && "source" in image && typeof image.source === "string") {
    // Sprite config: we only load the source image; sub-rect slicing is not supported.
    console.warn("[geostylerToMapLibre] Sprite images are not fully supported; using source only");
    return resolveImage(image.source);
  }
  return undefined;
}

/** Merges Icon + Text symbolizers of one rule into a single MapLibre symbol layer. */
function buildSymbolLayer(
  icons: IconSymbolizer[],
  texts: TextSymbolizer[],
  resolveImage: (ref: string) => string
): { layout: Record<string, unknown>; paint: Record<string, unknown> } {
  const layout: Record<string, unknown> = { ...SYMBOL_LAYOUT_DEFAULTS };
  const paint: Record<string, unknown> = {};

  if (icons.length > 1) {
    console.warn("[geostylerToMapLibre] Multiple Icon symbolizers in one rule; using the first");
  }
  const icon = icons[0];
  if (icon) {
    set(layout, "icon-image", resolveImageRef(icon.image, resolveImage));
    set(layout, "icon-size", icon.size);
    set(layout, "icon-rotate", icon.rotate);
    set(layout, "icon-offset", icon.offset);
    set(layout, "icon-anchor", icon.anchor);
    if (icon.allowOverlap !== undefined) layout["icon-allow-overlap"] = icon.allowOverlap;
    set(paint, "icon-opacity", icon.opacity);
    set(paint, "icon-color", icon.color);
    set(paint, "icon-halo-color", icon.haloColor);
    set(paint, "icon-halo-blur", icon.haloBlur);
  }

  if (texts.length > 1) {
    console.warn("[geostylerToMapLibre] Multiple Text symbolizers in one rule; using the first");
  }
  const text = texts[0];
  if (text) {
    set(layout, "text-field", translateLabel(text.label));
    set(layout, "text-font", text.font);
    set(layout, "text-size", text.size);
    set(layout, "text-offset", text.offset);
    set(layout, "text-anchor", text.anchor);
    set(layout, "text-max-width", text.maxWidth);
    set(layout, "text-transform", text.transform);
    if (text.allowOverlap !== undefined) layout["text-allow-overlap"] = text.allowOverlap;
    if (text.placement) layout["symbol-placement"] = text.placement;
    set(paint, "text-color", text.color);
    set(paint, "text-opacity", text.opacity);
    set(paint, "text-halo-color", text.haloColor);
    set(paint, "text-halo-width", text.haloWidth);
    set(paint, "text-halo-blur", text.haloBlur);
  }

  return { layout, paint };
}

/** Builds a MapLibre circle layer from a GeoStyler Mark symbolizer. */
function buildCircleLayer(mark: MarkSymbolizer): { layout: Record<string, unknown>; paint: Record<string, unknown> } {
  if (mark.wellKnownName && mark.wellKnownName !== "circle") {
    console.warn(`[geostylerToMapLibre] Mark "${mark.wellKnownName}" rendered as a circle (only "circle" is supported)`);
  }
  const paint: Record<string, unknown> = {};
  set(paint, "circle-radius", mark.radius);
  set(paint, "circle-color", mark.color);
  set(paint, "circle-opacity", mark.fillOpacity ?? mark.opacity);
  set(paint, "circle-stroke-color", mark.strokeColor);
  set(paint, "circle-stroke-width", mark.strokeWidth);
  set(paint, "circle-stroke-opacity", mark.strokeOpacity);
  set(paint, "circle-blur", mark.blur);
  return { layout: {}, paint };
}

/** Builds a MapLibre line layer from a GeoStyler Line symbolizer. */
function buildLineLayer(line: LineSymbolizer): { layout: Record<string, unknown>; paint: Record<string, unknown> } {
  const layout: Record<string, unknown> = { "line-join": line.join ?? "round", "line-cap": line.cap ?? "round" };
  const paint: Record<string, unknown> = {};
  set(paint, "line-color", line.color);
  set(paint, "line-width", line.width);
  set(paint, "line-opacity", line.opacity);
  set(paint, "line-dasharray", line.dasharray);
  return { layout, paint };
}

/** Builds a MapLibre fill layer from a GeoStyler Fill symbolizer. */
function buildFillLayer(fill: FillSymbolizer): { layout: Record<string, unknown>; paint: Record<string, unknown> } {
  const outlineWidth = fill.outlineWidth;
  if (typeof outlineWidth === "number" && outlineWidth > 1) {
    console.warn("[geostylerToMapLibre] Fill outlineWidth > 1 is not supported by MapLibre fill; rendered at 1px");
  }
  const paint: Record<string, unknown> = {};
  set(paint, "fill-color", fill.color);
  set(paint, "fill-outline-color", fill.outlineColor ?? fill.color);
  set(paint, "fill-opacity", fill.opacity);
  return { layout: {}, paint };
}

/**
 * Builds a MapLibre raster layer's paint from a GeoStyler Raster symbolizer.
 *
 * Tier 1 only (see the 9 Sep 2026 spatial-dataset styling plan, §4.1): every
 * field here is a MapLibre raster-layer *paint* property applied uniformly to
 * whatever pixels the tile already contains, so it works identically for a COG
 * or a pre-baked PMTiles raster. `colorMap` / `channelSelection` (Tier 2, real
 * value→colour recolouring) are deliberately NOT handled here — they need
 * per-pixel band access, which only `@geomatico/maplibre-cog-protocol`'s
 * `setColorFunction` provides (COG only), and are currently blocked on the
 * worker writing raw-value COGs for single-band rasters (plan §4.2, D-6).
 */
function buildRasterLayer(raster: RasterSymbolizer): { layout: Record<string, unknown>; paint: Record<string, unknown> } {
  const paint: Record<string, unknown> = {};
  set(paint, "raster-opacity", raster.opacity);
  set(paint, "raster-hue-rotate", raster.hueRotate);
  set(paint, "raster-brightness-min", raster.brightnessMin);
  set(paint, "raster-brightness-max", raster.brightnessMax);
  set(paint, "raster-saturation", raster.saturation);
  set(paint, "raster-contrast", raster.contrast);
  set(paint, "raster-resampling", raster.resampling);
  set(paint, "raster-fade-duration", raster.fadeDuration);
  return { layout: {}, paint };
}

/** Converts one GeoStyler rule into one or more MapLibre layer fragments. */
function ruleToLayers(rule: Rule, ruleIndex: number, resolveImage: (ref: string) => string): MapLibreStyleLayer[] {
  const filter = translateFilter(rule.filter);
  const { minzoom, maxzoom } = scaleDenominatorToZoom(rule.scaleDenominator);

  const icons = rule.symbolizers.filter((s): s is IconSymbolizer => s.kind === "Icon");
  const texts = rule.symbolizers.filter((s): s is TextSymbolizer => s.kind === "Text");
  const marks = rule.symbolizers.filter((s): s is MarkSymbolizer => s.kind === "Mark");
  const lines = rule.symbolizers.filter((s): s is LineSymbolizer => s.kind === "Line");
  const fills = rule.symbolizers.filter((s): s is FillSymbolizer => s.kind === "Fill");
  const rasters = rule.symbolizers.filter((s): s is RasterSymbolizer => s.kind === "Raster");

  const fragments: Array<{
    type: MapLibreLayerType;
    layout: Record<string, unknown>;
    paint: Record<string, unknown>;
    kinds: SymbolizerKind[];
  }> = [];

  // Icon + Text share a single symbol layer (MapLibre renders both together).
  if (icons.length || texts.length) {
    const { layout, paint } = buildSymbolLayer(icons, texts, resolveImage);
    const kinds: SymbolizerKind[] = [...icons.map(() => "Icon" as const), ...texts.map(() => "Text" as const)];
    fragments.push({ type: "symbol", layout, paint, kinds });
  }
  marks.forEach((m) => {
    const { layout, paint } = buildCircleLayer(m);
    fragments.push({ type: "circle", layout, paint, kinds: ["Mark"] });
  });
  lines.forEach((l) => {
    const { layout, paint } = buildLineLayer(l);
    fragments.push({ type: "line", layout, paint, kinds: ["Line"] });
  });
  fills.forEach((f) => {
    const { layout, paint } = buildFillLayer(f);
    fragments.push({ type: "fill", layout, paint, kinds: ["Fill"] });
  });
  rasters.forEach((r) => {
    const { layout, paint } = buildRasterLayer(r);
    fragments.push({ type: "raster", layout, paint, kinds: ["Raster"] });
  });

  const unsupported = rule.symbolizers.filter((s: Symbolizer) => !["Icon", "Text", "Mark", "Line", "Fill", "Raster"].includes(s.kind));
  if (unsupported.length) {
    console.warn(`[geostylerToMapLibre] Skipped unsupported symbolizer kind(s): ${unsupported.map((s) => s.kind).join(", ")}`);
  }

  return fragments.map((frag, partIndex) => {
    const layer: MapLibreStyleLayer = {
      type: frag.type,
      metadata: { rule: rule.name, ruleIndex, partIndex, symbolizerKinds: frag.kinds }
    };
    if (filter) layer.filter = filter;
    if (Object.keys(frag.layout).length) layer.layout = frag.layout;
    if (Object.keys(frag.paint).length) layer.paint = frag.paint;
    if (minzoom !== undefined) layer.minzoom = minzoom;
    if (maxzoom !== undefined) layer.maxzoom = maxzoom;
    return layer;
  });
}

/**
 * Converts a GeoStyler {@link Style} into an ordered array of MapLibre layer
 * fragments. Rule order is preserved (earlier rules render beneath later ones,
 * matching MapLibre paint order); attach `id`/`source` at the call site.
 */
export function geostylerToMapLibreLayers(style: Style, options: GeostylerToMapLibreOptions = {}): MapLibreStyleLayer[] {
  const resolveImage = options.resolveImage ?? resolveIconUrl;
  if (!style || !Array.isArray(style.rules)) return [];
  return style.rules.flatMap((rule, ruleIndex) => ruleToLayers(rule, ruleIndex, resolveImage));
}

/** A single MapLibre layer fragment compiled from several categorical rules. */
export interface CompiledCategoricalLayer {
  type: MapLibreLayerType;
  paint: Record<string, unknown>;
  layout?: Record<string, unknown>;
  minzoom?: number;
  maxzoom?: number;
}

/**
 * The property + value of a fragment filter shaped exactly like
 * `["==", ["get", property], value]` — what {@link translateFilter} produces for
 * a GeoStyler `["==", property, value]` rule filter. Anything else (compound
 * filters, `!=`, a literal-vs-literal comparison) returns null.
 */
function simpleEqFilter(filter: unknown): { property: string; value: string | number } | null {
  if (!Array.isArray(filter) || filter.length !== 3 || filter[0] !== "==") return null;
  const [, accessor, value] = filter;
  if (!Array.isArray(accessor) || accessor[0] !== "get" || typeof accessor[1] !== "string") return null;
  if (typeof value !== "string" && typeof value !== "number") return null;
  return { property: accessor[1], value };
}

/**
 * The property + negated values of a fragment filter shaped like
 * `["!", ["any", ["==", ["get", p], v], …]]` — what {@link translateFilter}
 * produces for the fallback rule's `["!", ["||", …]]` GeoStyler filter (see
 * `buildElseFilter` in `cmTableStyle.ts`). A hand-written single-value negation
 * (`["!", ["==", …]]`, no `any` wrapper) is accepted too.
 *
 * Returns null for anything else, including a negation mixing properties.
 */
function elseFilterValues(filter: unknown): { property: string; values: (string | number)[] } | null {
  if (!Array.isArray(filter) || filter.length !== 2 || filter[0] !== "!") return null;
  const inner = filter[1];
  if (!Array.isArray(inner)) return null;

  const clauses = inner[0] === "any" ? inner.slice(1) : [inner];
  if (!clauses.length) return null;

  let property: string | undefined;
  const values: (string | number)[] = [];
  for (const clause of clauses) {
    const eq = simpleEqFilter(clause);
    if (!eq) return null;
    if (property === undefined) property = eq.property;
    else if (property !== eq.property) return null;
    values.push(eq.value);
  }
  return property === undefined ? null : { property, values };
}

/** Structural equality for style values (scalars, arrays, plain objects). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
  if (a && b && typeof a === "object" && typeof b === "object") {
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    return ka.length === kb.length && ka.every((k) => deepEqual((a as any)[k], (b as any)[k]));
  }
  return false;
}

/** Whether a style value contains a `["zoom"]` expression at any depth. */
function containsZoomExpression(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  if (value.length === 1 && value[0] === "zoom") return true;
  return value.some(containsZoomExpression);
}

/**
 * Compiles several same-geometry, single-property-categorical MapLibre fragments
 * (one per GeoStyler rule) into ONE data-driven layer using `match` expressions,
 * instead of one MapLibre layer per rule.
 *
 * Why this exists: a tiled vector source (spatial-dataset PMTiles) pays for N
 * sibling layers with N filter evaluations per tile per frame. A single layer
 * with a `match` expression per paint/layout property is the idiomatic, cheaper
 * MapLibre pattern for "colour by category" against a vector-tile source — see
 * the 9 Sep 2026 spatial-dataset styling plan §3.2 (decision D-3). CM tables
 * don't need this: they render from a GeoJSON source they already re-fetch on
 * every style change, where sibling layers are cheap and simpler.
 *
 * Returns null when the fragments aren't expressible this way, so the caller
 * can fall back to its existing single-fragment (or sibling-layer) behaviour
 * rather than failing closed:
 *  - fewer than two fragments (nothing to compile — a lone rule is exactly
 *    today's single-fragment case);
 *  - fragments of different MapLibre layer types (mixed symbolizer kinds);
 *  - more than one candidate fallback rule (two unfiltered rules, or an
 *    unfiltered one alongside an "everything else" negation);
 *  - a fallback negation that doesn't negate exactly the categories present —
 *    `match`'s fallback arm covers *all* unmatched values, so a hand-edited
 *    negation over some other value set is not equivalent;
 *  - any other filtered fragment whose filter isn't a simple `==` on one property
 *    shared by every other filtered fragment (compound filters, per-rule zoom
 *    ranges expressed as a filter, multiple styling fields).
 *
 * The fallback rule arrives either unfiltered or carrying the
 * `["!", ["||", …]]` negation the writer emits (`buildElseFilter`); both mean
 * "the leftovers", which is exactly `match`'s fallback arm, so both compile.
 */
export function compileCategoricalMatchLayer(fragments: MapLibreStyleLayer[]): CompiledCategoricalLayer | null {
  if (fragments.length < 2) return null;
  const type = fragments[0].type;
  if (!fragments.every((f) => f.type === type)) return null;

  let property: string | undefined;
  const defaults: MapLibreStyleLayer[] = [];
  const categorized: { value: string | number; fragment: MapLibreStyleLayer }[] = [];
  const elses: { fragment: MapLibreStyleLayer; values: (string | number)[] }[] = [];

  for (const frag of fragments) {
    if (frag.filter === undefined) {
      defaults.push(frag);
      continue;
    }
    const eq = simpleEqFilter(frag.filter);
    if (eq) {
      if (property === undefined) property = eq.property;
      else if (property !== eq.property) return null;
      // MapLibre refuses a `match` whose branch labels repeat ("Branch labels
      // must be unique"), and refusing the layer takes the whole dataset with
      // it: the vector source loads its header, finds nothing consuming it, and
      // never fetches a tile. Two rules CAN carry the same category — the style
      // editor writes one rule per row and lets two rows name one value — so the
      // first rule for a value wins (the same resolution `buildUpdatedStyle`
      // applies when it rewrites the style) and the later ones are dropped
      // rather than compiled into a layer MapLibre will throw away.
      if (categorized.some((c) => c.value === eq.value)) {
        console.warn(`[geostylerToMapLibre] Duplicate category "${eq.value}" on "${eq.property}"; keeping the first rule's style`);
        continue;
      }
      categorized.push({ value: eq.value, fragment: frag });
      continue;
    }
    const negation = elseFilterValues(frag.filter);
    if (!negation) return null;
    if (property === undefined) property = negation.property;
    else if (property !== negation.property) return null;
    elses.push({ fragment: frag, values: negation.values });
  }

  if (defaults.length + elses.length > 1 || categorized.length === 0 || property === undefined) return null;

  // A negation only means the same thing as `match`'s fallback when it excludes
  // precisely the categories being compiled; anything else must keep its own layer.
  if (elses.length) {
    const categoryValues = new Set(categorized.map((c) => c.value));
    const negatedValues = new Set(elses[0].values);
    if (negatedValues.size !== categoryValues.size) return null;
    for (const value of negatedValues) if (!categoryValues.has(value)) return null;
  }

  const defaultFragment = defaults[0] ?? elses[0]?.fragment;

  const allFragments = defaultFragment ? [...categorized.map((c) => c.fragment), defaultFragment] : categorized.map((c) => c.fragment);
  const paintKeys = new Set<string>();
  const layoutKeys = new Set<string>();
  allFragments.forEach((f) => {
    Object.keys(f.paint ?? {}).forEach((k) => paintKeys.add(k));
    Object.keys(f.layout ?? {}).forEach((k) => layoutKeys.add(k));
  });

  /** One `match` (or, when every rule agrees, one static value) per property. */
  const compileValue = (getValue: (f: MapLibreStyleLayer) => unknown): unknown => {
    const defaultValue = defaultFragment ? getValue(defaultFragment) : undefined;
    const entries = categorized.map((c) => ({ value: c.value, output: getValue(c.fragment) })).filter((e) => e.output !== undefined);
    if (entries.length === 0) return defaultValue;

    // Collapse when every rule says the same thing, compared BY VALUE. The
    // outputs are freshly-built arrays and objects — an icon size the editor
    // writes to all six categories is six equal-but-distinct
    // `["step", ["zoom"], …]` arrays — so a reference check wraps them in a
    // `match` that means exactly the same thing, and for a zoom expression is
    // not even valid (below).
    const outputs = [...entries.map((e) => e.output), ...(defaultValue !== undefined ? [defaultValue] : [])];
    if (outputs.every((o) => deepEqual(o, outputs[0]))) return outputs[0];

    // MapLibre: a `["zoom"]` expression may only be the input to a TOP-LEVEL
    // `step`/`interpolate` — "zoom expression may only be used as input to a
    // top-level 'step' or 'interpolate' expression". Nested in a `match` arm it
    // makes the whole layer invalid, MapLibre refuses to add it, and the layer
    // then never renders while its vector source stops fetching tiles after the
    // header: the dataset silently vanishes the moment it is styled. A
    // per-category zoom curve cannot be expressed on one layer, so keep the
    // fallback rule's curve rather than emit something MapLibre will reject.
    if (outputs.some(containsZoomExpression)) {
      console.warn(
        "[geostylerToMapLibre] Per-category zoom expressions cannot be compiled into one layer; using the fallback rule's value"
      );
      return defaultValue !== undefined ? defaultValue : entries[entries.length - 1].output;
    }

    const expr: unknown[] = ["match", ["get", property]];
    entries.forEach((e) => expr.push(e.value, e.output));
    // `match` requires a fallback arm; fall back to the last category's own
    // output when there is genuinely no default rule rather than omitting it.
    expr.push(defaultValue !== undefined ? defaultValue : entries[entries.length - 1].output);
    return expr;
  };

  const paint: Record<string, unknown> = {};
  paintKeys.forEach((k) => {
    const v = compileValue((f) => f.paint?.[k]);
    if (v !== undefined) paint[k] = v;
  });

  const layout: Record<string, unknown> = {};
  layoutKeys.forEach((k) => {
    const v = compileValue((f) => f.layout?.[k]);
    if (v !== undefined) layout[k] = v;
  });

  return {
    type,
    paint,
    ...(Object.keys(layout).length ? { layout } : {}),
    ...(defaultFragment?.minzoom !== undefined ? { minzoom: defaultFragment.minzoom } : {}),
    ...(defaultFragment?.maxzoom !== undefined ? { maxzoom: defaultFragment.maxzoom } : {})
  };
}
